import { Injectable, signal } from "@angular/core";
import { RequestState } from "@shared/models";
import { map, Observable, tap } from "rxjs";
import { Hss } from "../hss-builder/models/hss";
import { UpdatesService } from "@shared/services";
import {
  HssChange,
  HssUpdateResponse,
} from "@shared/models/updates/hss-update-response";
import { HssLeg } from "../hss-builder/models/hss-leg";
import { HssDutyPeriod } from "../hss-builder/models/hss-duty-period";
import { DateTime, Duration, Interval } from "luxon";
import { HssItem } from "../hss-builder/models/hss-item";
import { HssRawFiltered } from "../hss-builder/models/hss-raw-filtered";
import { History } from "../hss-builder/models/history";

@Injectable({ providedIn: "root" })
export class HistoryService {
  readonly DATE_FORMAT = "LL/dd/yyyy HH:mm:ss";
  lastUpdated = signal<DateTime | null>(null);

  constructor(private updatesService: UpdatesService) {}

  getHssHistory(
    seqNumber: string,
    date: string,
    seat: string,
    forceRefresh?: boolean,
  ): Observable<RequestState<History>> {
    return this.updatesService
      .getHistory(seqNumber, date, seat, forceRefresh)
      .pipe(
        tap((response) => {
          if (response.response) {
            this.lastUpdated.set(DateTime.now());
          }
        }),
        tap((response) => console.log(response)),
        map((response) => this.generateHss(response, seat)),
        tap((history) => console.log(history)),
      );
  }

  // The raw HSS contains all cumulative changes including inactive or unused legs and duty periods.
  // The filtered HSS removes any unused legs and duty periods for display and total calculations.
  generateHss(
    updatesState: RequestState<HssUpdateResponse>,
    seat: string,
  ): RequestState<History> {
    if (!updatesState.response) {
      return updatesState.into<History>();
    }
    const hssList: HssRawFiltered[] = [];

    for (const update of updatesState.response.updates) {
      let hss = new Hss(seat, update.sequenceUpdateId);

      // Use copy of last raw HSS in list if list isn't empty
      if (hssList.length > 0) {
        hss = this.copyHss(hssList[hssList.length - 1].raw);
        hss.sequenceUpdateId = update.sequenceUpdateId;
      }

      hss.timestamp = update.timeStamp;
      hss.timestampHbt = update.timeStampHbt;

      for (const change of update.changes) {
        if (
          change.category === "SequenceUpdate" ||
          change.category === "Position"
        ) {
          this.handleSequenceChange(hss, change);
        }

        if (change.category === "DutyPeriod") {
          this.handleDutyPeriodChange(hss, change);
        }

        if (change.category === "Leg") {
          this.handleLegChange(hss, change);
        }
      }

      // Ignore this hss if no visible changes were made.
      if (this.didHssChange(hss)) {
        // Make sure duty periods and legs are sorted.
        hss.dutyPeriods.sort((a, b) => a.index - b.index);
        hss.legs.sort((a, b) => a.index - b.index);
        const filteredHss = this.getFilteredHss(hss);

        this.populateGndTime(filteredHss);
        this.populatePcAndTotals(filteredHss);
        hssList.push({ raw: hss, filtered: filteredHss });
      }
    }

    const history: History = {
      rawAndFilteredHssList: hssList,
      stationTimeZones: updatesState.response.stationTimeZones,
      raps: updatesState.response.raps,
    };

    return updatesState.into<History>(history);
  }

  // Modifies HSS with supplied duty period property change.
  private handleSequenceChange(hss: Hss, change: HssChange): void {
    this.applyChange(hss.header, change);
    this.applyChange(hss.summary, change);
  }

  // Modifies HSS with supplied duty period property change.
  private handleDutyPeriodChange(hss: Hss, change: HssChange): void {
    let foundDp: null | HssDutyPeriod = null;
    const dpIndex = change.propertyId;

    for (const dp of hss.dutyPeriods) {
      // Find applicable duty period.
      if (dp.index === dpIndex) {
        foundDp = dp;
      }
    }

    // Create new duty period if needed.
    if (!foundDp) {
      foundDp = new HssDutyPeriod(dpIndex);
      hss.dutyPeriods.push(foundDp);
    }

    this.applyChange(foundDp, change);
  }

  // Modifies HSS with supplied leg change.
  private handleLegChange(hss: Hss, change: HssChange): void {
    let foundLeg: null | HssLeg = null;

    for (const leg of hss.legs) {
      if (change.propertyId === leg.index) {
        foundLeg = leg;
        break;
      }
    }

    if (!foundLeg) {
      foundLeg = new HssLeg(change.propertyId);
      hss.legs.push(foundLeg);
    }

    // Set dpIndex for leg.
    if (change.property === "DutyPeriod") {
      foundLeg.dpIndex = parseInt(change.value);
    }

    this.applyChange(foundLeg, change);
  }

  // Apply property change to given object. Mutates object.
  private applyChange(outObject: Object, change: HssChange): void {
    for (const item of Object.entries(outObject)) {
      if (change.property === item[1].property) {
        item[1].text = change.value;
        item[1].didChange = true;
      }
    }
  }

  // Create a copy and set didChange to false for all fields.
  // This preserves the previous HSS data, but unsets the didChange property.
  private copyHss(hss: Hss): Hss {
    const copy: Hss = JSON.parse(JSON.stringify(hss));

    for (const prop of Object.entries(copy.header)) {
      if (prop[1].didChange) {
        prop[1].didChange = false;
      }
    }

    for (const prop of Object.entries(copy.summary)) {
      if (prop[1].didChange) {
        prop[1].didChange = false;
      }
    }

    for (const dp of copy.dutyPeriods) {
      for (const prop of Object.entries(dp)) {
        if (prop[1].didChange) {
          prop[1].didChange = false;
        }
      }
    }

    for (const leg of copy.legs) {
      for (const prop of Object.entries(leg)) {
        if (prop[1].didChange) {
          prop[1].didChange = false;
        }
      }
    }

    // Reset 'jump to' flags.
    hss.isAf = false;
    hss.isRo = false;
    hss.isPilotChange = false;
    hss.isCxl = false;

    return copy;
  }

  // Create deep copy of object.
  private copyObject<T>(object: T): T {
    return JSON.parse(JSON.stringify(object)) as T;
  }

  // Loop through properties to determine if any visual change occurred.
  private didHssChange(hss: Hss): boolean {
    for (const prop of Object.entries(hss.header)) {
      if (prop[1].didChange) {
        return true;
      }
    }

    for (const prop of Object.entries(hss.summary)) {
      if (prop[1].didChange) {
        return true;
      }
    }

    for (const dp of hss.dutyPeriods) {
      for (const prop of Object.entries(dp)) {
        if (prop[1].didChange) {
          return true;
        }
      }
    }

    for (const leg of hss.legs) {
      for (const prop of Object.entries(leg)) {
        if (prop[1].didChange) {
          // Special case for actualEquipCode, which tends to cause many undisplayed changes.
          if (prop[0] !== "actualEquipCode" && prop[0] !== "isEquipSub") {
            return true;
          }
        }
      }
    }

    return false;
  }

  // Returns a new HSS with unused legs and duty periods removed.
  // Also sets AF/RO/CXLD/Pilot Chg flags.
  private getFilteredHss(hss: Hss): Hss {
    const filteredHss = this.copyObject(hss);
    filteredHss.legs = [];
    filteredHss.dutyPeriods = [];
    filteredHss.isAf = false;
    filteredHss.isRo = false;
    filteredHss.isCxl = false;
    filteredHss.isPilotChange = false;

    if (filteredHss.header.empNum.didChange) {
      filteredHss.isPilotChange = true;
    }

    // Legs
    let isSeqEndFound = false;

    for (const leg of hss.legs) {
      // Skip over any legs after seq end found, except for cancelled legs.
      if (isSeqEndFound && leg.isCancelled.text === "False") {
        continue;
      }

      if (leg.isActive.text === "True" || leg.isCancelled.text === "True") {
        // Flag AF, RO, CXLD.
        if (leg.addCode.text === "AF" && leg.addCode.didChange) {
          filteredHss.isAf = true;
        }
        if (leg.addCode.text === "RO" && leg.addCode.didChange) {
          filteredHss.isRo = true;
        }
        if (leg.isCancelled.text === "True" && leg.isCancelled.didChange) {
          filteredHss.isCxl = true;
        }

        filteredHss.legs.push(this.copyObject<HssLeg>(leg));
      }

      if (leg.isSeqEnd.text === "True") {
        isSeqEndFound = true;
      }
    }

    // Duty periods
    const includedDps: number[] = []; // Make it easier to avoid duplicates.
    for (const dp of hss.dutyPeriods) {
      for (const leg of filteredHss.legs) {
        if (dp.index === leg.dpIndex && !includedDps.includes(dp.index)) {
          filteredHss.dutyPeriods.push(this.copyObject<HssDutyPeriod>(dp));
          includedDps.push(dp.index);
        }
      }
    }

    return filteredHss;
  }

  // Calculate and create change item for ground time between legs.
  // Mutates provided HSS.
  private populateGndTime(hss: Hss): void {
    for (let i = 0; i < hss.legs.length - 1; i++) {
      const leg = hss.legs[i];
      let nextLeg: HssLeg | null = null;

      // Find the next active leg within this duty period.
      if (
        hss.legs[i].isDpEnd.text === "False" &&
        hss.legs[i].isCancelled.text !== "True"
      ) {
        for (let j = i + 1; j < hss.legs.length; j++) {
          if (
            hss.legs[j].isActive.text === "True" &&
            hss.legs[j].isCancelled.text === "False" &&
            hss.legs[j].oalSurfaceAirline.text !== "SPT"
          ) {
            nextLeg = hss.legs[j];
            break;
          }
        }
      } else if (hss.legs[i].isCancelled.text === "True") {
        leg.groundTimeSched.text = "0.00";
        leg.groundTimeReSched.text = "0.00";
        leg.groundTimeActual.text = "";
      } else {
        leg.groundTimeSched.text = "";
        leg.groundTimeReSched.text = "";
        leg.groundTimeActual.text = "";
      }

      if (!nextLeg) {
        continue;
      }

      // SKD
      // Ignore ground time if RPT dh code.
      const dhCode = this.getDhCode(leg);

      if (dhCode === "RPT") {
        leg.groundTimeSched.text = "";
      } else if (leg.isDpEnd.text === "True") {
        leg.groundTimeSched.text = "";
        leg.groundTimeReSched.text = "";
        leg.groundTimeActual.text = "";
      } else {
        const skdArrTime = leg.arrTimeSched.text;
        const nextSkdDepTime = nextLeg.depTimeReSched.text.startsWith(
          "01/01/0001",
        )
          ? nextLeg.depTimeSched.text
          : nextLeg.depTimeReSched.text;
        let skdGroundTime = DateTime.fromFormat(
          nextSkdDepTime,
          this.DATE_FORMAT,
        )
          .diff(DateTime.fromFormat(skdArrTime, this.DATE_FORMAT))
          .toFormat("h.mm");

        leg.groundTimeSched.didChange = leg.arrTimeSched.didChange;

        leg.groundTimeSched.text = skdGroundTime;
      }

      // RSK
      if (!hss.legs[i].arrTimeReSched.text.startsWith("01/01/0001")) {
        const rskArrTime = leg.arrTimeReSched.text;
        let nextSkdDepTime = nextLeg.depTimeSched.text;

        if (nextLeg.isDepTimeReSched.text === "True") {
          nextSkdDepTime = nextLeg.depTimeReSched.text;
        }

        let rskGroundTime = DateTime.fromFormat(
          nextSkdDepTime,
          this.DATE_FORMAT,
        )
          .diff(DateTime.fromFormat(rskArrTime, this.DATE_FORMAT))
          .toFormat("h.mm");

        leg.groundTimeReSched.didChange = leg.arrTimeReSched.didChange;

        leg.groundTimeReSched.text = rskGroundTime;
      }

      // ACT
      if (
        !hss.legs[i].arrTimeActual.text.startsWith("01/01/0001") &&
        hss.legs[i].isCancelled.text === "False"
      ) {
        const actArrTime = leg.arrTimeActual.text;
        let nextDepTime = nextLeg.depTimeSched.text;

        if (!nextLeg.depTimeActual.text.startsWith("01/01/0001")) {
          nextDepTime = nextLeg.depTimeActual.text;
        }

        let actGroundTime = DateTime.fromFormat(nextDepTime, this.DATE_FORMAT)
          .diff(DateTime.fromFormat(actArrTime, this.DATE_FORMAT))
          .toFormat("h.mm");

        leg.groundTimeActual.didChange = leg.arrTimeActual.didChange;

        leg.groundTimeActual.text = actGroundTime;
      } else {
        leg.groundTimeActual.text = "";
      }
    }
  }

  // Calculate each duty period P/C and total P/C values. Modifies HSS.
  private populatePcAndTotals(hss: Hss) {
    let totalPcMinutes = 0;
    let totalPayMinutes = 0;
    let didChange = false;

    for (const dp of hss.dutyPeriods) {
      if (this.getActiveLegs(hss, dp.index) > 0) {
        if (!this.isLastDp(hss, dp.index)) {
          dp.payCreditDisplayed = this.getDpPayCredit(hss, dp);
        } else {
          dp.payCreditDisplayed = this.getLastDpPayCredit(hss, dp);
        }

        if (dp.payCreditDisplayed.didChange) {
          didChange = true;
        }

        totalPcMinutes += this.getDurationFromString(
          dp.payCreditDisplayed.text,
        ).as("minutes");
      }
    }

    hss.summary.payCreditDisplayed = {
      text: this.timeFromMinutes(`${totalPcMinutes}`),
      didChange,
      property: "",
    };

    const seqGreaterMinutes = this.getDurationFromString(
      hss.summary.greaterTime.text,
    ).as("minutes");
    totalPayMinutes = totalPcMinutes + seqGreaterMinutes;

    hss.summary.totalPay = {
      text: this.timeFromMinutes(`${totalPayMinutes}`),
      didChange,
      property: "",
    };
  }

  // Returns airline code if deadhead
  private getDhCode(leg: HssLeg): string {
    if (leg.isDhOrSurface.text === "True") {
      if (leg.oalSurfaceAirline.text !== "") {
        return leg.oalSurfaceAirline.text;
      } else {
        return "AA";
      }
    }

    return "";
  }

  // Find number of active legs for duty period.
  private getActiveLegs(hss: Hss, dpIndex: number): number {
    let count = 0;

    for (const leg of hss.legs) {
      if (leg.isActive.text === "True" && leg.dpIndex === dpIndex) {
        count++;
      }
    }

    return count;
  }

  // Check legs for SeqEnd flag.
  private isLastDp(hss: Hss, dpIndex: number): boolean {
    for (const leg of hss.legs) {
      if (leg.isSeqEnd.text === "True" && leg.dpIndex === dpIndex) {
        return true;
      }
    }
    return false;
  }

  private timeFromMinutes(minutes: string): string {
    const minutesInt = parseInt(minutes);
    const hours = Math.floor(minutesInt / 60);
    const remainingMinutes = minutesInt % 60;

    return `${hours}.${remainingMinutes.toString().padStart(2, "0")}`;
  }

  // Parses text of format (h)h.mm into Luxon duration.
  private getDurationFromString(text: string): Duration {
    const pattern = /(?<hours>\d{1,2})\.(?<minutes>\d{2})/;

    if (!pattern.test(text)) {
      return Duration.fromObject({ minute: 0 });
    }

    const minutes = parseInt(text.match(pattern)?.groups?.["minutes"] || "0");
    const hours = parseInt(text.match(pattern)?.groups?.["hours"] || "0");

    return Duration.fromObject({ hours, minutes });
  }

  // Determine duty period P/C and letter designation. This is used for all duty periods
  // except the last one. Will only include "C", "D" or "E" time.
  getDpPayCredit(seq: Hss, dp: HssDutyPeriod): HssItem {
    const pcMinutes = this.getDurationFromString(dp.payCreditActual.text).as(
      "minutes",
    );
    const pcDhMinutes = this.getDurationFromString(dp.payCreditDh.text).as(
      "minutes",
    );

    let contractLetter = "";

    if (pcMinutes > 0) {
      contractLetter = "E";

      if (seq.header.isCheckAirman.text === "True") {
        contractLetter = "C";
      }
    } else if (pcDhMinutes > 0) {
      contractLetter = "D";
    }

    const dpPayCredit = this.timeFromMinutes(`${pcDhMinutes + pcMinutes}`);

    return {
      text: `${dpPayCredit}${contractLetter}`,
      didChange: dp.payCreditActual.didChange || dp.payCreditDh.didChange,
      property: "",
    };
  }

  // Calculates the last duty period P/C time which may include "F" or "G" time.
  private getLastDpPayCredit(seq: Hss, dp: HssDutyPeriod): HssItem {
    // Get D/E time first.
    const dpPcItem = this.getDpPayCredit(seq, dp);
    const dpPcMinutes = this.getDurationFromString(dpPcItem.text).as("minutes");
    const seqPcMinutes = this.getDurationFromString(
      seq.summary.payCreditActual.text,
    ).as("minutes");

    // Extract contract letter from P/C string.
    const pattern = /\d{1,2}\.\d{2}(?<contractLetter>[A-Z]{0,1})/;

    let pcSuffix =
      dpPcItem.text.match(pattern)?.groups?.["contractLetter"] || "";

    if (seqPcMinutes > 0) {
      const tafbMinutes = this.getDurationFromString(this.getTafb(seq).text).as(
        "minutes",
      );
      const totalFltTimeMinutes = this.getDurationFromString(
        seq.summary.greaterTime.text,
      ).as("minutes");

      // F time is TAFB / 3.5.
      const fTime = tafbMinutes / 3.5;
      const fPcTime = fTime - totalFltTimeMinutes;

      if (fPcTime <= 0) {
        // Must be G time if F time doesn't apply.
        pcSuffix = "G";
      } else {
        // Calculate G time and determine if it's larger than F time.
        // G time is 5:15 (315 minutes) per calendar day.
        const calendarDays = this.getSeqCalendarDays(seq);
        const gTime = 315 * calendarDays;
        const gPcTime = gTime - totalFltTimeMinutes;

        if (gPcTime > fPcTime) {
          pcSuffix = "G";
        } else {
          pcSuffix = "F";
        }
      }
    }

    let pcString = this.timeFromMinutes(`${dpPcMinutes + seqPcMinutes}`);

    return {
      text: `${pcString}${pcSuffix}`,
      didChange: dpPcItem.didChange || seq.summary.payCreditActual.didChange,
      property: "",
    };
  }

  private getTafb(seq: Hss): HssItem {
    // Find last duty period by finding leg with sequence end flag.
    // let lastDpIndex = -1;
    // let lastLeg: HssLeg | null = null;
    const debriefMinutes = seq.header.isInternational.text === "True" ? 30 : 15;
    let seqStartTime: DateTime | undefined;
    let seqEndTime: DateTime | undefined;
    let didChange = false;

    // Find the first duty period with at least one active leg.
    for (const dp of seq.dutyPeriods) {
      if (this.getActiveLegs(seq, dp.index) > 0) {
        seqStartTime = DateTime.fromFormat(dp.startTime.text, this.DATE_FORMAT);
        if (dp.startTime.didChange) {
          didChange = true;
        }
        break;
      }
    }

    // Need to use latest of last leg SKD/RSK/ACT. Using DP endtime is inaccurate in certain scenarios.
    for (const leg of seq.legs) {
      if (leg.isSeqEnd.text === "True") {
        const arrivalTimes: DateTime[] = [];
        // lastLeg = leg;
        // lastDpIndex = parseInt(leg.dutyPeriod.text);
        if (leg.isArrTimeSched.text === "True") {
          arrivalTimes.push(
            DateTime.fromFormat(leg.arrTimeSched.text, this.DATE_FORMAT).plus({
              minutes: debriefMinutes,
            }),
          );
        }
        if (leg.isArrTimeReSched.text === "True") {
          arrivalTimes.push(
            DateTime.fromFormat(leg.arrTimeReSched.text, this.DATE_FORMAT).plus(
              { minutes: debriefMinutes },
            ),
          );
        }
        if (leg.isArrTimeActual.text === "True") {
          arrivalTimes.push(
            DateTime.fromFormat(leg.arrTimeActual.text, this.DATE_FORMAT).plus({
              minutes: debriefMinutes,
            }),
          );
        }

        seqEndTime = DateTime.max(...arrivalTimes);
        break;
      }
    }

    if (seqStartTime && seqEndTime) {
      return {
        text: seqEndTime.diff(seqStartTime).toFormat("h.mm"),
        property: "",
        didChange,
      };
    }

    return {
      text: "ERROR",
      property: "",
      didChange: false,
    };
  }

  private getSeqCalendarDays(seq: Hss): number {
    const seqStartTime = this.getSeqStartTime(seq);
    const seqEndTime = this.getSeqEndTime(seq);

    if (seqStartTime && seqEndTime) {
      let calDays = Interval.fromDateTimes(seqStartTime, seqEndTime).count(
        "days",
      );

      // If sequence ends between 00:00 - 01:59, subtract a calendar day.
      if (seqEndTime.hour < 2) {
        calDays -= 1;
      }

      return calDays;
    }

    return 0;
  }

  // Find first leg of sequence and return local start time including report.
  private getSeqStartTime(seq: Hss): DateTime | undefined {
    let depTime: DateTime | undefined;

    for (const leg of seq.legs) {
      if (leg.isActive.text === "True") {
        if (leg.isDepTimeReSched.text === "True") {
          depTime = DateTime.fromFormat(
            leg.depTimeReSchedLocal.text,
            this.DATE_FORMAT,
          );
        } else {
          depTime = DateTime.fromFormat(
            leg.depTimeSchedLocal.text,
            this.DATE_FORMAT,
          );
        }

        // Subtract an hour for report time unless it's a deadhead, then 30 minutes.
        if (leg.isDhOrSurface.text === "True") {
          depTime = depTime.minus({ minutes: 30 });
        } else {
          depTime = depTime.minus({ hours: 1 });
        }

        return depTime;
      }
    }

    return depTime;
  }

  // Find last leg of sequence and return local end time including debrief.
  private getSeqEndTime(seq: Hss): DateTime | null {
    for (let i = seq.legs.length - 1; i >= 0; i--) {
      const leg = seq.legs[i];
      // Collect valid end times. The latest time will be used (SKD, RSK, ACT).
      const endTimes: DateTime[] = [];
      let debriefTime = 30;

      if (seq.header.isInternational.text === "False") {
        debriefTime = 15;
      }

      if (leg.isSeqEnd.text === "True") {
        if (leg.arrTimeSchedLocal.text !== "") {
          endTimes.push(
            DateTime.fromFormat(
              leg.arrTimeSchedLocal.text,
              this.DATE_FORMAT,
            ).plus({ minutes: debriefTime }),
          );
        }

        if (leg.arrTimeReSchedLocal.text !== "") {
          endTimes.push(
            DateTime.fromFormat(
              leg.arrTimeReSchedLocal.text,
              this.DATE_FORMAT,
            ).plus({ minutes: debriefTime }),
          );
        }

        if (leg.arrTimeActualLocal.text !== "") {
          endTimes.push(
            DateTime.fromFormat(
              leg.arrTimeActualLocal.text,
              this.DATE_FORMAT,
            ).plus({ minutes: debriefTime }),
          );
        }

        return DateTime.max(...endTimes);
      }
    }

    return null;
  }
}
